import { Modal, Box, Button, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import { useProperty } from "@services";
import { ToastMessageContext } from "@context";
import OTPInput from "react-otp-input";
import { MOBILE_START_WITH_05 } from "../../../../utils/regex";
import { useIsMutating } from "@tanstack/react-query";
import { modalBoxStyle, modalContainerStyle } from "../../../../utils";
import { BackgroundCompressed } from "../../../../utils/image-consts";
import { StepProgress } from "@components";

interface GathernConnectModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const GathernConnectModal = ({
  open,
  onClose,
  onSuccess,
}: GathernConnectModalProps) => {
  const { t } = useTranslation();
  const mutating = useIsMutating();
  const [step, setStep] = useState<2 | 3>(2);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const { showError } = useContext(ToastMessageContext);

  const { gathrenLoginMutate, gathrenVerifyOtpMutate } = useProperty();

  const { mutate: gathrenVerifyOtp } = gathrenVerifyOtpMutate({
    onError: () => {
      showError(t("ERRORS.SOMETHING_WENT_WRONG"));
    },
    onSuccess: () => {
      handleClose();
      onSuccess();
    },
  });

  const { mutate: gathrenLogin } = gathrenLoginMutate({
    onError: () => {
      showError(t("ERRORS.SOMETHING_WENT_WRONG"));
    },
    onSuccess: () => {
      setStep(3);
    },
  });

  const sendOtp = () => {
    if (new RegExp(MOBILE_START_WITH_05).test(phone)) {
      const payload: any = {
        phone: phone,
      };
      gathrenLogin(payload);
    } else {
      showError(t("ERRORS.INVALID_MOBILE"));
    }
  };

  const onOtpSubmit = () => {
    const payload: any = {
      phone: phone,
      otp: otp,
    };
    gathrenVerifyOtp(payload);
  };

  const handleClose = () => {
    setStep(2);
    setPhone("");
    setOtp("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={modalContainerStyle}
      aria-labelledby="gathern-connect-modal"
      aria-describedby="gathern-connect-description"
    >
      <Box
        sx={{
          ...modalBoxStyle,
          width: { xs: "800px", lg: "1000px" },
          height: "90vh",
          display: { xs: "flex", lg: "grid" },
          gridTemplateColumns: { lg: "repeat(11,1fr)" },
          p: 0,
        }}
      >
        {/* Left Side - Purple Background with Illustration */}
        <Box
          sx={{
            gridColumn: { lg: "span 5 / span 5" },
            height: { xs: "fit-content", lg: "auto" },
            flexShrink: 0,
            px: 2,
            py: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 1,
            bgcolor: "primary.main",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundImage: `url(${BackgroundCompressed})`,
          }}
        >
          <Typography color={"white"} fontSize={{ xs: "24px", lg: "32px" }}>
            {t("onBoardingJourny.gathernConnectTitle")}
          </Typography>
          <Typography color={"white"} fontSize={{ xs: "14px", lg: "16px" }}>
            {t("onBoardingJourny.gathernConnectDescription")}
          </Typography>
        </Box>

        {/* Right Side - White Form Section */}
        <Box
          sx={{
            p: 2,
            gridColumn: { lg: "span 6 / span 6" },
            flexGrow: 1,
            height: "inherit",
            overflowY: "auto",
            display: "flex",
            gap: 2,
            flexDirection: "column",
            "::-webkit-scrollbar": {
              width: "10px",
              background: "#F0F0F0",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#5B3FFF",
              borderRadius: "5px",
            },
          }}
        >
          <StepProgress step={step} steps={2} />

          {step === 3 ? (
            <Box
              sx={{ flex: 1, pt: 4, pb: 2, display: "flex", flexDirection: "column" }}
            >
              <Typography color={"#0F172A"} fontSize={"20px"}>
                {t("ADD_PROPERTY.OTP_STEP")}
              </Typography>
              <Typography color={"#8E98A1"} fontSize={"16px"}>
                {t("ADD_PROPERTY.OTP_STEP_DESCRIPTION")}
              </Typography>

              <Box sx={{ width: "200px", div: { direction: "ltr" }, mt: 2 }}>
                <OTPInput
                  value={otp}
                  containerStyle={"gap-4 mt-2 rtl:-ms-2"}
                  inputStyle={"rounded border border-[#9CA3AF] w-10 h-12 flex text-center text-xl "}
                  shouldAutoFocus={true}
                  inputType={"tel"}
                  skipDefaultStyles={true}
                  onChange={setOtp}
                  numInputs={4}
                  renderInput={(props) => <input {...props} />}
                />
              </Box>
              <Box sx={{ mt: "auto", display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                  size={"small"}
                  variant={"contained"}
                  onClick={onOtpSubmit}
                  disabled={otp?.length != 4 || mutating > 0}
                >
                  {t("ADD_PROPERTY.CONFIRM")}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{ flex: 1, pt: 4, pb: 2, display: "flex", flexDirection: "column" }}
            >
              <Typography color={"#0F172A"} fontSize={"20px"}>
                {t("ADD_PROPERTY.PHONE_STEP_TITLE")}
              </Typography>
              <Typography color={"#8E98A1"} fontSize={"16px"}>
                {t("ADD_PROPERTY.PHONE_STEP_DESCRIPTION")}
              </Typography>
              <TextField
                id="phone"
                className="w-full rounded-3xl"
                InputProps={{ sx: { borderRadius: 3 } }}
                style={{ borderRadius: "24px !important", color: "#9CA3AF", borderColor: "#9CA3AF", marginTop: 16 }}
                placeholder={`0500000000`}
                value={phone}
                variant="outlined"
                onChange={(e) => setPhone(e.target.value)}
              />
              <Box sx={{ mt: "auto", display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                  size={"small"}
                  variant={"contained"}
                  onClick={sendOtp}
                  disabled={phone?.length != 10 || mutating > 0}
                >
                  {t("ADD_PROPERTY.SEND_OTP")}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default GathernConnectModal;
